import { Link } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import {
  Check, X, ExternalLink, ArrowRight, Sparkles, Users, HardDrive,
  Video, Calendar, FileText, Shield, Mail,
} from "lucide-react";
import {
  GOOGLE_WORKSPACE_REFERRAL_URL,
  GOOGLE_WORKSPACE_PROMO_CODE,
  trackWorkspaceReferralClick,
} from "../lib/partners";

// Referral CTA button — used in multiple places on this page. Every click
// fires a GA4 event so we can watch the funnel independently of Google's
// own attribution (they only tell us about paid conversions after 90 days).
function WorkspaceCTA({ children, source, size = "md", variant = "primary", testid }) {
  const base = "inline-flex items-center gap-2 font-bold rounded-xl transition-all";
  const sizes = { sm: "px-4 py-2 text-sm", md: "px-6 py-3 text-sm", lg: "px-8 py-4 text-base" };
  const variants = {
    primary: "text-white bg-[#F26953] hover:bg-[#dd5a47] shadow-lg hover:shadow-xl hover:-translate-y-0.5",
    outline: "text-[#0F172A] bg-white border-2 border-[#0F172A] hover:bg-[#0F172A] hover:text-white",
  };
  return (
    <a
      href={GOOGLE_WORKSPACE_REFERRAL_URL}
      target="_blank"
      rel="noopener noreferrer sponsored"
      onClick={() => trackWorkspaceReferralClick(source)}
      data-testid={testid || "workspace-cta"}
      className={`${base} ${sizes[size]} ${variants[variant]}`}
    >
      {children}
      <ExternalLink className="h-4 w-4 opacity-70" />
    </a>
  );
}

// Feature comparison row
function CompareRow({ feature, oly, workspace, note }) {
  return (
    <tr className="border-b border-[#E2E8F0] last:border-0">
      <td className="py-3 pr-4 text-sm text-[#0F172A]">
        {feature}
        {note && <div className="text-[11px] text-slate-400 mt-0.5">{note}</div>}
      </td>
      <td className="py-3 px-4 text-center">
        {oly === true ? <Check className="h-5 w-5 text-emerald-500 mx-auto" /> :
         oly === false ? <X className="h-4 w-4 text-slate-300 mx-auto" /> :
         <span className="text-xs text-[#475569]">{oly}</span>}
      </td>
      <td className="py-3 pl-4 text-center bg-slate-50/50">
        {workspace === true ? <Check className="h-5 w-5 text-emerald-500 mx-auto" /> :
         workspace === false ? <X className="h-4 w-4 text-slate-300 mx-auto" /> :
         <span className="text-xs text-[#475569]">{workspace}</span>}
      </td>
    </tr>
  );
}

export default function WorkspaceUpgrade() {
  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Header />
      <main>
        {/* Hero */}
        <section className="max-w-5xl mx-auto px-5 py-16 md:py-24">
          <div className="text-center">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#FEE7E1] text-[#F26953] text-xs font-semibold uppercase tracking-wider mb-6">
              <Sparkles className="h-3.5 w-3.5" />
              Recommended by oly Inbox
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#0F172A] tracking-tight leading-[1.05]">
              Outgrew forwarding?
              <br />
              <span className="text-[#F26953]">Try Google Workspace.</span>
            </h1>
            <p className="mt-6 text-lg text-[#475569] max-w-2xl mx-auto">
              If you need Gmail on your own domain, Drive, Docs, Calendar and Meet for your team —
              Google Workspace is the natural next step. Start with a 14-day free trial.
            </p>
            {GOOGLE_WORKSPACE_PROMO_CODE && (
              <div className="mt-6 inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-50 border border-emerald-200">
                <span className="text-xs uppercase tracking-wider font-bold text-emerald-700">Promo code</span>
                <code className="mono font-bold text-emerald-800">{GOOGLE_WORKSPACE_PROMO_CODE}</code>
                <span className="text-xs text-emerald-700">10% off your first year</span>
              </div>
            )}
            <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
              <WorkspaceCTA source="upgrade-hero" size="lg" testid="workspace-hero-cta">
                Start free 14-day trial
              </WorkspaceCTA>
              <a
                href="#compare"
                className="inline-flex items-center gap-1 px-6 py-4 text-sm font-semibold text-[#475569] hover:text-[#0F172A]"
              >
                Compare features
                <ArrowRight className="h-4 w-4" />
              </a>
            </div>
            <p className="mt-4 text-xs text-slate-400 max-w-md mx-auto">
              14 days free · Cancel anytime · No credit card required to start · Business Starter from $6/user/mo
            </p>
          </div>
        </section>

        {/* Feature icons row */}
        <section className="max-w-5xl mx-auto px-5 pb-16">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { icon: Mail,      title: "Custom Gmail",   body: "you@yourdomain.com with the full Gmail app + 30 GB inbox" },
              { icon: Video,     title: "Meet + Chat",    body: "HD video calls up to 100 participants, team chat channels" },
              { icon: HardDrive, title: "Drive storage",  body: "Shared drives for teams, up to 5 TB per user" },
              { icon: Calendar,  title: "Team calendar",  body: "Shared calendars, meeting rooms, resource booking" },
            ].map((f) => (
              <div key={f.title} className="bg-white rounded-2xl border border-[#E2E8F0] p-5">
                <div className="h-10 w-10 rounded-xl bg-[#FEE7E1] text-[#F26953] flex items-center justify-center mb-3">
                  <f.icon className="h-5 w-5" />
                </div>
                <h3 className="font-semibold text-[#0F172A]">{f.title}</h3>
                <p className="mt-1 text-xs text-[#475569] leading-relaxed">{f.body}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Comparison table */}
        <section id="compare" className="max-w-4xl mx-auto px-5 pb-16">
          <h2 className="text-2xl md:text-3xl font-bold text-[#0F172A] tracking-tight text-center">
            oly Inbox vs Google Workspace
          </h2>
          <p className="mt-2 text-sm text-[#475569] text-center max-w-xl mx-auto">
            oly Inbox is perfect when all you need is forwarding + a shared inbox. Workspace is
            the right call when you need full productivity tools for a team.
          </p>
          <div className="mt-8 rounded-2xl border border-[#E2E8F0] overflow-hidden bg-white">
            <table className="w-full text-sm">
              <thead className="bg-[#0F172A] text-white">
                <tr>
                  <th className="text-left py-3 px-4 font-semibold">Feature</th>
                  <th className="text-center py-3 px-4 font-semibold">
                    <span className="inline-flex items-center gap-1"><Mail className="h-4 w-4 text-[#F26953]" />oly Inbox</span>
                  </th>
                  <th className="text-center py-3 px-4 font-semibold bg-slate-800/50">
                    <span className="inline-flex items-center gap-1"><Sparkles className="h-4 w-4 text-amber-300" />Workspace</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                <CompareRow feature="Email at your own domain" oly={true} workspace={true} />
                <CompareRow feature="Forward to any existing inbox" oly={true} workspace={true} />
                <CompareRow feature="Reply & send from your alias" oly={true} workspace={true} />
                <CompareRow feature="Full Gmail app + 30 GB inbox" oly={false} workspace={true} />
                <CompareRow feature="Google Drive, Docs, Sheets, Slides" oly={false} workspace={true} />
                <CompareRow feature="Google Meet + Calendar for teams" oly={false} workspace={true} />
                <CompareRow feature="Shared inboxes for a team" oly="Coming soon" workspace={true} />
                <CompareRow feature="Multiple domains per account" oly={true} workspace={true} />
                <CompareRow feature="Pricing (per mailbox)" oly="From $3/mo" workspace="From $6/user/mo" />
                <CompareRow
                  feature="Setup time"
                  oly="~3 min"
                  workspace="~30 min"
                  note="DNS + user provisioning"
                />
                <CompareRow
                  feature="Best for"
                  oly="Solopreneurs, side projects, forwarding"
                  workspace="Teams, agencies, growing SMBs"
                />
              </tbody>
            </table>
          </div>

          <div className="mt-8 flex flex-col items-center gap-4">
            <WorkspaceCTA source="upgrade-compare" size="lg" testid="workspace-compare-cta">
              Start Google Workspace free trial
            </WorkspaceCTA>
            <Link to="/dashboard" className="text-sm text-[#475569] hover:text-[#0F172A]">
              or stay with oly Inbox →
            </Link>
          </div>
        </section>

        {/* FAQ + disclosure */}
        <section className="max-w-3xl mx-auto px-5 pb-24">
          <h2 className="text-xl font-bold text-[#0F172A] tracking-tight mb-4">
            Frequently asked
          </h2>
          <div className="space-y-3">
            {[
              {
                q: "Do I have to leave oly Inbox to use Workspace?",
                a: "Not at all. Many customers use both — oly Inbox handles cheap wildcard forwarding for old domains, while Workspace runs their primary work email. You can point different domains at each.",
              },
              {
                q: "How long is the free trial?",
                a: "14 days. You can cancel anytime during the trial without being charged.",
              },
              {
                q: "Can I keep my existing email?",
                a: "Yes. Workspace can import from Gmail, Outlook, or other providers as part of setup.",
              },
              {
                q: "What if Workspace is too much?",
                a: "Come right back. oly Inbox works with any inbox as your forwarding destination, including Workspace — so nothing is lost.",
              },
            ].map((item) => (
              <details key={item.q} className="group bg-white border border-[#E2E8F0] rounded-xl overflow-hidden">
                <summary className="cursor-pointer px-5 py-4 text-sm font-semibold text-[#0F172A] hover:bg-slate-50 list-none flex items-center justify-between">
                  {item.q}
                  <ArrowRight className="h-4 w-4 text-slate-400 group-open:rotate-90 transition-transform" />
                </summary>
                <div className="px-5 pb-4 text-sm text-[#475569] leading-relaxed">
                  {item.a}
                </div>
              </details>
            ))}
          </div>

          {/* FTC-required affiliate disclosure */}
          <div className="mt-10 p-5 rounded-2xl bg-slate-100/70 border border-slate-200">
            <div className="flex items-start gap-3">
              <Shield className="h-4 w-4 text-slate-500 flex-shrink-0 mt-0.5" />
              <p className="text-[11px] text-slate-500 leading-relaxed">
                <strong className="text-slate-700">Referral disclosure:</strong> oly Inbox is a
                member of the Google Workspace Referral Program. If you sign up for Google
                Workspace using the link above, we may earn a one-time referral bonus after your
                account remains active for 90 days. This has no effect on the price you pay and
                does not influence which product we recommend — Workspace is genuinely the right
                tool for teams needing more than forwarding.
              </p>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
